import { useState, useEffect } from 'react'
import { fetchRest } from '@/lib/hook/tanstackQuery'
import { mapHitToProduct } from '@/lib/search/mapHitToProduct'
import { ApiQueryEndpoint } from '@/src/ApiQueryEndpoint'

// Blocs de recommandation, servis par le moteur interne.
//
// Mapping des modeles -> types de l'endpoint REST `recommend`
// (pharme_recommend, cf. back docs/ES_RECOMMEND.md) :
//   bought-together                  -> also_bought  (cache co-achats)
//   related-products/looking-similar -> similar      (more_like_this ES)
//   trending-items                   -> best_sellers (sans company_id = top ventes globales)
//   companyId fourni                 -> best_sellers&company_id (best-sellers du vendeur)
//   personalized, discovery, reorder, season -> types du meme nom (nouveaux blocs)
//   bought-with                      -> bought_with&product_ids, avec company_id pour le franco
//
// La visibilite (usergroups, storefront) est appliquee cote serveur via l'auth, comme les
// regles du test A/B : un bras B retire le deja achete et plafonne par marque.

type RecommendModel =
  | 'bought-together'
  | 'trending-items'
  | 'related-products'
  | 'looking-similar'
  | 'personalized'
  | 'discovery'
  | 'reorder'
  | 'season'
  | 'bought-with'

type UseInternalRecommendOptions = {
  model: RecommendModel
  objectIDs?: string[]
  // Best-sellers d'un vendeur
  companyId?: string | number
  maxRecommendations?: number
  excludeObjectIDs?: string[]
  enabled?: boolean
}

type RecommendType =
  | 'also_bought'
  | 'best_sellers'
  | 'similar'
  | 'personalized'
  | 'discovery'
  | 'reorder'
  | 'season'
  | 'bought_with'

// Le serveur complète sa liste quand il retire ces produits : c'est mieux que de les
// retirer ici après coup, ce qui raccourcirait le bloc.
const excludeParam = (ids: string[]) =>
  ids.length > 0 ? { exclude_product_ids: ids.join(',') } : {}

type RecommendResponse = { products?: Record<string, any>[]; season_categories?: string[] }

const modelToType = (model: RecommendModel, companyId?: string | number): RecommendType => {
  if (model === 'personalized' || model === 'discovery' || model === 'reorder' || model === 'season') {
    return model
  }
  if (model === 'bought-with') return 'bought_with'
  if (companyId) return 'best_sellers'
  if (model === 'bought-together') return 'also_bought'
  if (model === 'trending-items') return 'best_sellers'
  return 'similar' // related-products | looking-similar
}

export const useInternalRecommend = ({
  model,
  objectIDs,
  companyId,
  maxRecommendations = 8,
  excludeObjectIDs = [],
  enabled = true,
}: UseInternalRecommendOptions) => {
  const [products, setProducts] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [seasonCategories, setSeasonCategories] = useState<string[]>([])

  // Serialize arrays to stable strings for useEffect dependencies
  const objectIDsKey = JSON.stringify(objectIDs ?? [])
  const excludeKey = JSON.stringify(excludeObjectIDs)

  useEffect(() => {
    if (!enabled) return

    const type = modelToType(model, companyId)
    const parsedObjectIDs: string[] = JSON.parse(objectIDsKey)
    const needsObjectIDs = type === 'also_bought' || type === 'similar'
    if ((needsObjectIDs || type === 'bought_with') && parsedObjectIDs.length === 0) return

    const fetchRecommendations = async () => {
      setLoading(true)
      try {
        const parsedExclude: string[] = JSON.parse(excludeKey)

        // Une requete par objectID , une seule pour best_sellers
        const requests = needsObjectIDs
          ? parsedObjectIDs.map(objectID =>
              fetchRest<RecommendResponse>({
                endpoint: ApiQueryEndpoint.CATALOG.RECOMMEND.endpoint,
                queriesParams: {
                  type,
                  product_id: objectID,
                  limit: maxRecommendations,
                  ...excludeParam(parsedExclude),
                },
              })
            )
          : [
              fetchRest<RecommendResponse>({
                endpoint: ApiQueryEndpoint.CATALOG.RECOMMEND.endpoint,
                queriesParams: {
                  type,
                  ...(companyId ? { company_id: companyId } : {}),
                  ...(type === 'bought_with' ? { product_ids: parsedObjectIDs.join(',') } : {}),
                  limit: maxRecommendations,
                  ...excludeParam(parsedExclude),
                },
              }),
            ]

        const responses = await Promise.all(requests)

        // Merge hits from all requests, deduplicate by product_id
        const excludeSet = new Set(parsedExclude.map(String))
        const seen = new Set<string>()
        const allHits: any[] = []

        for (const response of responses) {
          for (const hit of response?.products ?? []) {
            const id = String(hit.product_id)
            if (!seen.has(id) && !excludeSet.has(id)) {
              seen.add(id)
              allHits.push(hit)
            }
          }
        }

        // Map to Product shape and limit
        const mapped = allHits.slice(0, maxRecommendations).map(mapHitToProduct)
        setProducts(mapped)
        setSeasonCategories(responses[0]?.season_categories ?? [])
      } catch {
        // Vide silencieux : les blocs recommend se masquent, pas d'erreur bloquante
        setProducts([])
      } finally {
        setLoading(false)
      }
    }

    fetchRecommendations().catch(() => {})
  }, [model, objectIDsKey, companyId, maxRecommendations, excludeKey, enabled])

  return { products, loading, seasonCategories }
}
